import React, { useEffect, useRef, useState } from "react";
import { Formik, FormikProps } from "formik";
import * as Yup from "yup";
import {
  Button,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  IconButton,
} from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import { useDispatch } from "react-redux";
import {
  registerUser,
  sendRegisterOtp,
  verifyPan,
} from "../../redux/user/userSlice";
import toast from "react-hot-toast";
import { LuCheck, LuAlertCircle, LuX } from "react-icons/lu";
import {
  OFF_WHITE,
  DARK_NAVY,
  DARK_NAVY_LIGHT,
  INPUT_BORDER_FOCUS,
  modalInputSx,
} from "../../constants/layout";
import AppLoader from "../AppLoader/AppLoader";

const BLACK = "#000000";
const ICON_BLUE = INPUT_BORDER_FOCUS;
const CARD_SHADOW = "0 2px 16px rgba(0, 0, 0, 0.06)";
const PROFILE_FONT = '"Inter", sans-serif';
const BORDER = "#E8E8E8";

type ModalFieldProps = {
  label: string;
  id: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  type?: string;
  disabled?: boolean;
  error?: string;
  touched?: boolean | string;
};

const ModalField = ({
  label,
  id,
  name,
  value,
  onChange,
  placeholder,
  type = "text",
  disabled = false,
  error,
  touched,
}: ModalFieldProps) => {
  const hasError = !!(error && touched);

  return (
    <Box sx={{ flex: 1, minWidth: 220 }}>
      <Typography
        component="label"
        htmlFor={id}
        sx={{
          display: "block",
          fontFamily: PROFILE_FONT,
          fontSize: 14,
          fontWeight: 500,
          color: hasError ? "#d32f2f" : DARK_NAVY,
          mb: 1,
        }}
      >
        {label}
      </Typography>
      <Box
        component="input"
        id={id}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        sx={{
          ...modalInputSx,
          ...(hasError
            ? {
                borderColor: "#d32f2f",
                "&:focus": { borderColor: "#d32f2f", boxShadow: "none" },
              }
            : {}),
        }}
      />
      {hasError ? (
        <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 12, color: "#d32f2f", mt: 0.5 }}>
          {error}
        </Typography>
      ) : null}
    </Box>
  );
};

const primaryButtonSx = {
  fontFamily: PROFILE_FONT,
  textTransform: "none" as const,
  fontWeight: 600,
  fontSize: 14,
  bgcolor: ICON_BLUE,
  color: "#ffffff",
  borderRadius: "10px",
  boxShadow: "none",
  "&:hover": { bgcolor: "#16304f", boxShadow: "none" },
  "&:disabled": { bgcolor: "#A8B8C8", color: "#ffffff" },
};

const validationSchema = Yup.object({
  firstName: Yup.string().required("First Name is required"),
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
  mobile: Yup.string()
    .matches(/^[0-9]{10}$/, "Mobile number must be 10 digits")
    .required("Mobile number is required"),
  pancard: Yup.string()
    .matches(/[A-Z]{5}[0-9]{4}[A-Z]{1}/, "Invalid PAN card number")
    .required("PAN card number is required"),
  password: Yup.string()
    .matches(
      /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d@$.!%*#?&]{8,}$/,
      "Password must be at least 8 characters long, include at least one letter and one number"
    )
    .required("Password is required"),
});

type FormValues = {
  firstName: string;
  email: string;
  mobile: string;
  pancard: string;
  userId: string | null;
  registeredBy: string | null;
  password: string;
};

export type RegisterFormProps = {
  open?: boolean;
  onClose?: () => void;
  onSuccess?: () => void;
};

const getInitialValues = (): FormValues => ({
  firstName: "",
  email: "",
  mobile: "",
  pancard: "",
  userId: localStorage.getItem("AumUserID"),
  registeredBy: localStorage.getItem("userType"),
  password: "password#123",
});

const RegisterForm = ({ open = false, onClose, onSuccess }: RegisterFormProps) => {
  const dispatch = useDispatch<any>();
  const formikRef = useRef<FormikProps<FormValues>>(null);

  const [loading, setLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState({
    pan: false,
    email: false,
    mobile: false,
  });
  const [otp, setOtp] = useState({ email: "", mobile: "" });
  const [pendingVerifications, setPendingVerifications] = useState({
    email: false,
    mobile: false,
  });
  const [otpDialogOpen, setOtpDialogOpen] = useState(false);

  const resetFormState = () => {
    setVerificationStatus({ pan: false, email: false, mobile: false });
    setOtp({ email: "", mobile: "" });
    setPendingVerifications({ email: false, mobile: false });
    setOtpDialogOpen(false);
    formikRef.current?.resetForm({ values: getInitialValues() });
  };

  useEffect(() => {
    if (!open) resetFormState();
  }, [open]);

  const handleVerifyPAN = async (payload: {
    pancardNumber: string;
    fullName: string;
  }) => {
    setIsVerifying(true);
    try {
      const res = await dispatch(verifyPan(payload));
      if (res?.error?.message) {
        toast.error(res?.payload?.message);
        return;
      }
      setVerificationStatus((prev) => ({ ...prev, pan: true }));
      toast.success("PAN verified successfully");
    } catch {
      toast.error("PAN verification failed");
    } finally {
      setIsVerifying(false);
    }
  };

  const handleSendOTP = async (values: FormValues) => {
    setIsVerifying(true);
    setLoading(true);
    try {
      setPendingVerifications({ email: true, mobile: true });
      await dispatch(
        sendRegisterOtp({
          firstName: values.firstName,
          email: values.email,
          mobile: values.mobile,
        })
      );
      setOtpDialogOpen(true);
      toast.success("OTP sent to your email and mobile");
    } catch {
      toast.error("Failed to send OTP");
    } finally {
      setIsVerifying(false);
      setLoading(false);
    }
  };

  const handleRegistrationSuccess = () => {
    setOtpDialogOpen(false);
    setOtp({ email: "", mobile: "" });
    toast.success("User registered successfully");
    resetFormState();
    onSuccess?.();
    onClose?.();
  };

  const addUser = async () => {
    if (!verificationStatus.pan) {
      toast.error("Please complete all verifications before registering");
      return;
    }

    try {
      setLoading(true);
      const response = await dispatch(
        registerUser({
          emailOTP: otp.email,
          mobileOTP: otp.mobile,
          ...formikRef.current?.values,
        })
      );
      if (response?.error?.message) {
        toast.error("User registration failed");
        return;
      }
      handleRegistrationSuccess();
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "An unknown error occurred";
      toast.error(`Error: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const formFields = (
    <Formik
      innerRef={formikRef}
      initialValues={getInitialValues()}
      validationSchema={validationSchema}
      onSubmit={(values, { setSubmitting }) => {
        handleSendOTP(values);
        setSubmitting(false);
      }}
    >
      {({ values, handleChange, handleSubmit, errors, touched, setFieldValue }) => (
        <form onSubmit={handleSubmit}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <ModalField
                label="First Name"
                id="firstName"
                name="firstName"
                value={values.firstName}
                onChange={handleChange}
                placeholder="Enter first name"
                error={errors.firstName}
                touched={touched.firstName}
              />
              <ModalField
                label="Email"
                id="email"
                name="email"
                type="email"
                value={values.email}
                onChange={handleChange}
                placeholder="Enter email address"
                disabled={verificationStatus.email}
                error={errors.email}
                touched={touched.email}
              />
            </Box>

            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-end" }}>
              <ModalField
                label="Mobile Number"
                id="mobile"
                name="mobile"
                value={values.mobile}
                onChange={handleChange}
                placeholder="Enter mobile number"
                disabled={verificationStatus.mobile}
                error={errors.mobile}
                touched={touched.mobile}
              />
              <Box
                sx={{
                  flex: 1,
                  minWidth: 220,
                  display: "flex",
                  gap: 1,
                  alignItems: "flex-end",
                }}
              >
                <ModalField
                  label="PAN Card"
                  id="pancard"
                  name="pancard"
                  value={values.pancard}
                  onChange={(e) => {
                    setFieldValue("pancard", e.target.value.toUpperCase());
                  }}
                  placeholder="Enter PAN number"
                  disabled={verificationStatus.pan}
                  error={errors.pancard}
                  touched={touched.pancard}
                />
                <Button
                  variant="contained"
                  onClick={() =>
                    handleVerifyPAN({
                      pancardNumber: values.pancard,
                      fullName: values.firstName,
                    })
                  }
                  disabled={
                    !values.pancard ||
                    !!errors.pancard ||
                    verificationStatus.pan ||
                    isVerifying
                  }
                  sx={{
                    ...primaryButtonSx,
                    minWidth: 100,
                    minHeight: 46,
                    mb: errors.pancard && touched.pancard ? 2.5 : 0,
                    bgcolor: verificationStatus.pan ? "#08991F" : ICON_BLUE,
                    "&:hover": {
                      bgcolor: verificationStatus.pan ? "#067a19" : "#16304f",
                    },
                  }}
                >
                  {verificationStatus.pan ? (
                    <LuCheck size={20} />
                  ) : isVerifying ? (
                    <CircularProgress size={22} color="inherit" />
                  ) : (
                    "Verify"
                  )}
                </Button>
              </Box>
            </Box>

            <AnimatePresence>
              {(!verificationStatus.pan ||
                !verificationStatus.email ||
                !verificationStatus.mobile) && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                >
                  <Alert
                    severity="info"
                    icon={<LuAlertCircle size={20} color={ICON_BLUE} />}
                    sx={{
                      fontFamily: PROFILE_FONT,
                      bgcolor: OFF_WHITE,
                      color: DARK_NAVY_LIGHT,
                      borderRadius: "12px",
                      border: `1px solid ${BORDER}`,
                      "& .MuiAlert-icon": { color: ICON_BLUE },
                    }}
                  >
                    Please complete all verifications before proceeding with registration.
                  </Alert>
                </motion.div>
              )}
            </AnimatePresence>

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, pt: 0.5 }}>
              <Button
                onClick={onClose}
                sx={{
                  fontFamily: PROFILE_FONT,
                  textTransform: "none",
                  color: DARK_NAVY_LIGHT,
                  fontWeight: 500,
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                sx={{ ...primaryButtonSx, px: 3, py: 1.25, minWidth: 160 }}
                disabled={!verificationStatus.pan || isVerifying}
              >
                Verify & Continue
              </Button>
            </Box>
          </Box>
        </form>
      )}
    </Formik>
  );

  return (
    <>
      <AppLoader loading={loading} />

      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: "16px",
            fontFamily: PROFILE_FONT,
            boxShadow: CARD_SHADOW,
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: PROFILE_FONT,
            fontWeight: 700,
            fontSize: 20,
            color: DARK_NAVY,
            pr: 6,
            pb: 1,
          }}
        >
          Add Investor
          <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 13, color: DARK_NAVY_LIGHT, fontWeight: 400, mt: 0.5 }}>
            Register a new investor by verifying PAN and OTP.
          </Typography>
          <IconButton
            onClick={onClose}
            aria-label="Close"
            sx={{
              position: "absolute",
              right: 12,
              top: 12,
              color: DARK_NAVY_LIGHT,
              "&:hover": { bgcolor: OFF_WHITE, color: ICON_BLUE },
            }}
          >
            <LuX size={20} />
          </IconButton>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2.5,
            maxHeight: "70vh",
            overflowY: "auto",
          }}
        >
          {formFields}
        </DialogContent>
      </Dialog>

      <Dialog
        open={otpDialogOpen}
        onClose={() => setOtpDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            width: "100%",
            maxWidth: 440,
            p: 1,
            fontFamily: PROFILE_FONT,
            boxShadow: CARD_SHADOW,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: PROFILE_FONT,
            fontWeight: 700,
            fontSize: 20,
            color: DARK_NAVY,
            pb: 1,
          }}
        >
          Verify OTP
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 14, color: DARK_NAVY_LIGHT, mb: 2.5, lineHeight: 1.5 }}>
            Enter the OTP sent to{" "}
            <Box component="span" sx={{ fontWeight: 600, color: BLACK }}>
              {formikRef.current?.values.email || "your email"}
            </Box>{" "}
            and{" "}
            <Box component="span" sx={{ fontWeight: 600, color: BLACK }}>
              {formikRef.current?.values.mobile || "your mobile"}
            </Box>
            .
          </Typography>

          {pendingVerifications.email ? (
            <Box sx={{ mb: 2 }}>
              <Typography
                component="label"
                sx={{
                  display: "block",
                  fontFamily: PROFILE_FONT,
                  fontSize: 14,
                  fontWeight: 500,
                  color: DARK_NAVY,
                  mb: 1,
                }}
              >
                Email OTP
              </Typography>
              <Box
                component="input"
                type="text"
                value={otp.email}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setOtp((prev) => ({ ...prev, email: e.target.value }))
                }
                placeholder="Enter email OTP"
                sx={modalInputSx}
              />
            </Box>
          ) : null}

          {pendingVerifications.mobile ? (
            <Box>
              <Typography
                component="label"
                sx={{
                  display: "block",
                  fontFamily: PROFILE_FONT,
                  fontSize: 14,
                  fontWeight: 500,
                  color: DARK_NAVY,
                  mb: 1,
                }}
              >
                Mobile OTP
              </Typography>
              <Box
                component="input"
                type="text"
                value={otp.mobile}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setOtp((prev) => ({ ...prev, mobile: e.target.value }))
                }
                placeholder="Enter mobile OTP"
                sx={modalInputSx}
              />
            </Box>
          ) : null}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={() => setOtpDialogOpen(false)}
            sx={{
              fontFamily: PROFILE_FONT,
              textTransform: "none",
              color: DARK_NAVY_LIGHT,
              fontWeight: 500,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={addUser}
            variant="contained"
            disabled={
              (pendingVerifications.email && !otp.email) ||
              (pendingVerifications.mobile && !otp.mobile) ||
              isVerifying
            }
            sx={{ ...primaryButtonSx, px: 2.5 }}
          >
            {isVerifying ? <CircularProgress size={22} color="inherit" /> : "Verify OTP"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RegisterForm;
